import { useT } from "../lib/i18n.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { LIMITI, percentualeDaTrascinamento, restringi } from "../lib/split.ts";

/**
 * Due pannelli affiancati con un divisore spostabile.
 *
 * Una proporzione fissa va bene in media e mai bene davvero: chi scrive un
 * compose lungo vuole più spazio a sinistra, chi sistema quindici variabili lo
 * vuole a destra. La posizione viene ricordata, così la scelta si fa una volta.
 *
 * Il divisore è raggiungibile da tastiera e si sposta con le frecce: un elemento
 * che si può usare solo trascinandolo esclude chi non usa il mouse.
 */

export interface PannelloDivisoProps {
  sinistra: React.ReactNode;
  destra: React.ReactNode;
  /** Chiave di memorizzazione: pannelli diversi ricordano proporzioni diverse. */
  memoria: string;
  /** Sotto questa larghezza i pannelli si impilano e il divisore scompare. */
  minimoAffiancato?: number;
}

/** Passo dello spostamento da tastiera, in punti percentuali. */
const PASSO = 2;

const leggiMemoria = (chiave: string): number => {
  const salvato = window.localStorage.getItem(`portman.split.${chiave}`);

  return salvato === null ? LIMITI.predefinito : restringi(Number(salvato));
};

export function PannelloDiviso({
  sinistra,
  destra,
  memoria,
  minimoAffiancato = 1024,
}: PannelloDivisoProps) {
  const t = useT();
  const contenitoreRef = useRef<HTMLDivElement>(null);
  const [percentuale, setPercentuale] = useState(() => leggiMemoria(memoria));
  const [affiancato, setAffiancato] = useState(() => window.innerWidth >= minimoAffiancato);
  const [inTrascinamento, setInTrascinamento] = useState(false);

  // Su finestre strette affiancare due editor li rende entrambi inutilizzabili.
  useEffect(() => {
    const aggiorna = () => setAffiancato(window.innerWidth >= minimoAffiancato);
    window.addEventListener("resize", aggiorna);

    return () => window.removeEventListener("resize", aggiorna);
  }, [minimoAffiancato]);

  const salva = useCallback(
    (valore: number) => {
      setPercentuale(valore);
      window.localStorage.setItem(`portman.split.${memoria}`, String(valore));
    },
    [memoria],
  );

  const aggiornaDaPuntatore = useCallback(
    (clientX: number) => {
      const rettangolo = contenitoreRef.current?.getBoundingClientRect();
      if (!rettangolo) return;

      setPercentuale(
        percentualeDaTrascinamento(clientX, {
          sinistra: rettangolo.left,
          larghezza: rettangolo.width,
        }),
      );
    },
    [],
  );

  // Gli ascoltatori vivono sulla finestra e non sul divisore: il puntatore, in
  // un trascinamento veloce, esce dall'elemento e i suoi eventi si perderebbero.
  useEffect(() => {
    if (!inTrascinamento) return;

    const muovi = (evento: PointerEvent) => aggiornaDaPuntatore(evento.clientX);
    const termina = () => {
      setInTrascinamento(false);
      salva(percentuale);
    };

    window.addEventListener("pointermove", muovi);
    window.addEventListener("pointerup", termina);
    window.addEventListener("pointercancel", termina);

    return () => {
      window.removeEventListener("pointermove", muovi);
      window.removeEventListener("pointerup", termina);
      window.removeEventListener("pointercancel", termina);
    };
  }, [inTrascinamento, aggiornaDaPuntatore, salva, percentuale]);

  if (!affiancato) {
    return (
      <div className="flex flex-col gap-4">
        {sinistra}
        {destra}
      </div>
    );
  }

  return (
    <div ref={contenitoreRef} className="flex min-h-0 items-stretch">
      <div className="min-w-0" style={{ width: `${percentuale}%` }}>
        {sinistra}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t.t("pannelli.ridimensiona")}
        aria-valuenow={Math.round(percentuale)}
        aria-valuemin={LIMITI.minimo}
        aria-valuemax={LIMITI.massimo}
        tabIndex={0}
        onPointerDown={(evento) => {
          evento.preventDefault();
          setInTrascinamento(true);
        }}
        onDoubleClick={() => salva(LIMITI.predefinito)}
        onKeyDown={(evento) => {
          if (evento.key === "ArrowLeft") salva(restringi(percentuale - PASSO));
          else if (evento.key === "ArrowRight") salva(restringi(percentuale + PASSO));
          else if (evento.key === "Home") salva(LIMITI.predefinito);
          else return;

          evento.preventDefault();
        }}
        // L'area sensibile è più larga della linea visibile: prenderla al primo
        // colpo non deve richiedere precisione.
        className="group relative mx-1 w-2 shrink-0 cursor-col-resize touch-none"
        title={t.t("pannelli.suggerimento")}
      >
        <div
          className={
            inTrascinamento
              ? "absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded bg-accent-500"
              : "absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded bg-[var(--bordo)] transition-colors group-hover:bg-accent-500/60"
          }
        />
      </div>

      <div className="min-w-0 flex-1">{destra}</div>
    </div>
  );
}
